/**
    MIT License

    Copyright (c) Microsoft Corporation.

    Permission is hereby granted, free of charge, to any person obtaining a copy
    of this software and associated documentation files (the "Software"), to deal
    in the Software without restriction, including without limitation the rights
    to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
    copies of the Software, and to permit persons to whom the Software is
    furnished to do so, subject to the following conditions:

    The above copyright notice and this permission notice shall be included in all
    copies or substantial portions of the Software.

    THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
    IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
    FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
    AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
    LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
    OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
    SOFTWARE
 */

// This source repo is unmaintained, so we're copying the code here to make it easier to maintain.
// https://github.com/Azure/fetch-event-source

/**
 * Represents a message sent in an event stream
 * https://developer.mozilla.org/en-US/docs/Web/API/Server-sent_events/Using_server-sent_events#Event_stream_format
 *
 * @typedef {object} EventSourceMessage
 * @property {string} id - The event ID to set the EventSource object's last event ID value.
 * @property {string} event - A string identifying the type of event described.
 * @property {string} data - The event data
 * @property {string} [retry] - The reconnection interval (in milliseconds) to wait before retrying the connection
 */

/**
 * @typedef {Object} FetchEventSourceInitExt
 * @property {Record<string, string>|() => Record<string, string>|() => Promise<Record<string, string>>} [headers] - The request headers. FetchEventSource only supports the Record<string,string> format.
 * @property {(response: Response) => Promise<void>} [onopen] - Called when a response is received. Use this to validate that the response actually matches what you expect (and throw if it doesn't.) If not provided, will default to a basic validation to ensure the content-type is text/event-stream.
 * @property {(ev: EventSourceMessage) => void} [onmessage] - Called when a message is received. NOTE: Unlike the default browser EventSource.onmessage, this callback is called for _all_ events, even ones with a custom `event` field.
 * @property {() => void} [onclose] - Called when a response finishes. If you don't expect the server to kill the connection, you can throw an exception here and retry using onerror.
 * @property {(err: any) => number | null | undefined | void} [onerror] - Called when there is any error making the request / processing messages / handling callbacks etc. Use this to control the retry strategy: if the error is fatal, rethrow the error inside the callback to stop the entire operation. Otherwise, you can return an interval (in milliseconds) after which the request will automatically retry (with the last-event-id). If this callback is not specified, or it returns undefined, fetchEventSource will treat every error as retriable and will try again after 1 second.
 * @property {boolean} [openWhenHidden] - If true, will keep the request open even if the document is hidden. By default, fetchEventSource will close the request and reopen it automatically when the document becomes visible again.
 * @property {number} [retryInterval] - The initial retry interval to use in milliseconds. If onerror returns a value, it will override this value. Defaults to 1000.
 * @property {typeof fetch} [fetch] - The Fetch function to use. Defaults to window.fetch.
 */

/** @typedef {Omit<RequestInit, 'headers'> & FetchEventSourceInitExt} FetchEventSourceInit */

const DefaultRetryInterval = 1000;
const LastEventId = 'last-event-id';

export const EventStreamContentType = 'text/event-stream';
export class RetriableError extends Error {
  retryInterval;

  constructor(retryInterval = DefaultRetryInterval) {
    super();
    this.retryInterval = retryInterval;
  }
}
export class FatalError extends Error {}

/**
 * Fetches an event source.
 *
 * @export
 * @param {RequestInfo} input - The request information.
 * @param {FetchEventSourceInit} options - The initialization options.
 * @returns {Promise<void>} A promise that will be resolved when the operation completes.
 */
export function fetchEventSource(
  input,
  {
    signal: inputSignal,
    headers: inputHeaders,
    onopen: inputOnOpen,
    onmessage,
    onclose,
    onerror,
    openWhenHidden,
    retryInterval: inputRetryInterval,
    fetch: inputFetch,
    ...rest
  }
) {
  let getHeaders;
  if (typeof inputHeaders === 'function') {
    getHeaders = inputHeaders;
  } else {
    getHeaders = () => ({ ...inputHeaders });
  }

  return new Promise((resolve, reject) => {
    let curRequestController;

    function onVisibilityChange() {
      curRequestController.abort(); // close existing request on every visibility change

      if (!globalThis.document?.hidden) {
        create(); // page is now visible again, recreate request.
      }
    }

    if (globalThis.document && !openWhenHidden) {
      globalThis.document?.addEventListener('visibilitychange', onVisibilityChange);
    }

    let retryInterval = inputRetryInterval || DefaultRetryInterval;
    let retryTimer = 0;

    function dispose() {
      if (globalThis.document && !openWhenHidden) {
        globalThis.document?.removeEventListener('visibilitychange', onVisibilityChange);
      }

      globalThis.clearTimeout(retryTimer);
      curRequestController.abort();
    }

    // if the incoming signal aborts, dispose resources and resolve:
    inputSignal?.addEventListener('abort', () => {
      dispose();
      resolve(); // don't waste time constructing/logging errors
    });

    const fetch = inputFetch || globalThis.fetch;
    const onopen = inputOnOpen || defaultOnOpen;

    async function create() {
      const headers = await getHeaders();

      if (!headers.accept) {
        headers.accept = EventStreamContentType;
      }

      curRequestController = new AbortController();

      try {
        const response = await fetch(input, {
          ...rest,
          headers,
          signal: curRequestController.signal,
        });

        await onopen(response);

        await getBytes(
          response.body,
          getLines(
            getMessages(
              id => {
                if (id) {
                  // store the id and send it back on the next retry:
                  headers[LastEventId] = id;
                } else {
                  // don't send the last-event-id header anymore:
                  delete headers[LastEventId];
                }
              },
              retry => {
                retryInterval = retry;
              },
              onmessage
            )
          )
        );

        if (onclose) {
          onclose();
        }
        dispose();
        resolve();
      } catch (err) {
        if (!curRequestController.signal.aborted) {
          // if we haven't aborted the request ourselves:
          try {
            // check if we need to retry:
            const interval = onerror ? onerror(err) : retryInterval;
            globalThis.clearTimeout(retryTimer);
            retryTimer = globalThis.setTimeout(create, interval);
          } catch (innerErr) {
            // we should not retry anymore:
            dispose();
            reject(innerErr);
          }
        }
      }
    }

    create();
  });
}

/**
 * @param {Response} response
 */
function defaultOnOpen(response) {
  const contentType = response.headers.get('content-type');
  if (!contentType?.startsWith(EventStreamContentType)) {
    throw new Error(`Expected content-type to be ${EventStreamContentType}, Actual: ${contentType}`);
  }
}

/**
 * Converts a ReadableStream into a callback pattern.
 *
 * @export
 * @param {ReadableStream<Uint8Array>} stream
 * @param {(arr: Uint8Array) => void} onChunk
 * @returns {Promise<void>}  A promise that will be resolved when the stream closes.
 */
async function getBytes(stream, onChunk) {
  const reader = stream.getReader();
  /** @type {ReadableStreamReadResult<Uint8Array>} */
  let result;

  while (!(result = await reader.read()).done) {
    onChunk(result.value);
  }
}

/**
 * Parses arbitrary byte chunks into EventSource line buffers.
 * Each line should be of the format "field: value" and ends with \r, \n, or \r\n.
 * @export
 * @param {(line: Uint8Array, fieldLength: number) => void} onLine - A function that will be called on each new EventSource line.
 */
function getLines(onLine) {
  /** @type {Uint8Array|undefined} */
  let buffer;
  /** @type {number} */
  let position;
  /** @type {number} */
  let fieldLength;
  /** @type {boolean} */
  let discardTrailingNewline = false;

  return function onChunk(arr) {
    if (buffer === undefined) {
      buffer = arr;
      position = 0;
      fieldLength = -1;
    } else {
      buffer = concat(buffer, arr);
    }

    const bufLength = buffer.length;
    let lineStart = 0; // index where the current line starts
    while (position < bufLength) {
      if (discardTrailingNewline) {
        if (buffer[position] === 10) {
          lineStart = ++position; // skip to next char
        }

        discardTrailingNewline = false;
      }

      // start looking forward till the end of line:
      let lineEnd = -1; // index of the \r or \n char
      for (; position < bufLength && lineEnd === -1; ++position) {
        // eslint-disable-next-line default-case
        switch (buffer[position]) {
          case 58: // ':'
            if (fieldLength === -1) {
              // first colon in line
              fieldLength = position - lineStart;
            }
            break;
          case 13: // '\r'
            discardTrailingNewline = true;
          // eslint-disable-next-line no-fallthrough
          case 10: // '\n'
            lineEnd = position;
            break;
        }
      }

      if (lineEnd === -1) {
        // We reached the end of the buffer but the line hasn't ended.
        // Wait for the next arr and then continue parsing:
        break;
      }

      // we've reached the line end, send it out:
      onLine(buffer.subarray(lineStart, lineEnd), fieldLength);
      lineStart = position; // we're now on the next line
      fieldLength = -1;
    }

    if (lineStart === bufLength) {
      buffer = undefined; // we've finished reading it
    } else if (lineStart !== 0) {
      // Create a new view into buffer beginning at lineStart so we don't
      // need to copy over the previous lines when we get the new arr:
      buffer = buffer.subarray(lineStart);
      position -= lineStart;
    }
  };
}

/**
 * Parses line buffers into EventSourceMessages.
 * @param {function(id: string): void} onId - A function that will be called on each `id` field.
 * @param {function(retry: number): void} onRetry - A function that will be called on each `retry` field.
 * @param {function(msg: EventSourceMessage): void} [onMessage] - A function that will be called on each message.
 * @returns {function(line: Uint8Array, fieldLength: number): void} - A function that should be called for each incoming line buffer.
 */
function getMessages(onId, onRetry, onMessage) {
  let message = newMessage();
  const decoder = new TextDecoder();

  // return a function that can process each incoming line buffer:
  return function onLine(line, fieldLength) {
    if (line.length === 0) {
      // empty line denotes end of message. Trigger the callback and start a new message:
      if (onMessage && (message.data || message.event || message.id)) {
        onMessage(message);
      }
      message = newMessage();
    } else if (fieldLength > 0) {
      // exclude comments and lines with no values
      // line is of format "<field>:<value>" or "<field>: <value>"
      // https://html.spec.whatwg.org/multipage/server-sent-events.html#event-stream-interpretation
      const field = decoder.decode(line.subarray(0, fieldLength));
      const valueOffset = fieldLength + (line[fieldLength + 1] === 32 /* ' ' */ ? 2 : 1);
      const value = decoder.decode(line.subarray(valueOffset));

      // eslint-disable-next-line default-case
      switch (field) {
        case 'data':
          // if this message already has data, append the new value to the old.
          // otherwise, just set to the new value:
          message.data = message.data ? message.data + '\n' + value : value; // otherwise,
          break;
        case 'event':
          message.event = value;
          break;
        case 'id':
          onId((message.id = value));
          break;
        case 'retry': {
          const retry = parseInt(value, 10);

          if (!isNaN(retry)) {
            // per spec, ignore non-integers
            onRetry((message.retry = retry));
          }
        }
      }
    }
  };
}
/**
 * Concatenates two Uint8Array instances.
 *
 * @param {Uint8Array} a - The first Uint8Array.
 * @param {Uint8Array} b - The second Uint8Array.
 * @returns {Uint8Array} - The concatenated Uint8Array.
 */
function concat(a, b) {
  const res = new Uint8Array(a.length + b.length);
  res.set(a);
  res.set(b, a.length);
  return res;
}

/**
 * Creates a new EventSourceMessage object.
 *
 * @returns {EventSourceMessage} - The new EventSourceMessage.
 */
function newMessage() {
  // data, event, and id must be initialized to empty strings:
  // https://html.spec.whatwg.org/multipage/server-sent-events.html#event-stream-interpretation
  // retry should be initialized to undefined so we return a consistent shape
  // to the js engine all the time: https://mathiasbynens.be/notes/shapes-ics#takeaways
  return {
    data: '',
    event: '',
    id: '',
    retry: undefined,
  };
}
